import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './PageDesign.module.css';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import { playUISound } from '../../utils/UISoundEffects';

// Design System Color Tokens
const DARK_MODE_COLORS = {
  backgrounds: [
    { name: 'Primary Background', value: '#080812', usage: 'Main page background' },
    { name: 'Secondary Background', value: '#0a0a18', usage: 'Section backgrounds' },
    { name: 'Tertiary Background', value: '#12122a', usage: 'Card backgrounds' },
    { name: 'Surface', value: '#1a1a2e', usage: 'Elevated surfaces' },
    { name: 'Surface Alt', value: '#16213e', usage: 'Alternate surfaces' },
  ],
  text: [
    { name: 'Primary', value: '#f0f0f5', usage: 'Main text' },
    { name: 'Secondary', value: 'rgba(240, 240, 245, 0.8)', usage: 'Supporting text' },
    { name: 'Muted', value: 'rgba(240, 240, 245, 0.5)', usage: 'Subtle text' },
    { name: 'Accent', value: 'rgba(240, 245, 255, 0.7)', usage: 'Celestial text' },
  ],
  borders: [
    { name: 'Subtle', value: 'rgba(255, 255, 255, 0.06)', usage: 'Faint borders' },
    { name: 'Default', value: 'rgba(255, 255, 255, 0.1)', usage: 'Standard borders' },
    { name: 'Strong', value: 'rgba(255, 255, 255, 0.15)', usage: 'Emphasized borders' },
    { name: 'Glow Frame', value: 'rgba(240, 245, 255, 0.85)', usage: 'Parallax frame' },
    { name: 'Star Accent', value: 'rgba(200, 180, 255, 0.3)', usage: 'Star decorations' },
  ],
  accents: [
    { name: 'Purple Glow', value: 'rgba(200, 180, 255, 0.6)', usage: 'Hover glows' },
    { name: 'Celestial Blue', value: 'rgba(107, 124, 138, 0.3)', usage: 'Subtle blue glow' },
    { name: 'Link Blue', value: '#60a5fa', usage: 'Interactive links' },
  ],
};

const LIGHT_MODE_COLORS = {
  backgrounds: [
    { name: 'Primary Background', value: '#f5f3ef', usage: 'Main page background (warm cream)' },
    { name: 'Secondary Background', value: '#eae6df', usage: 'Section backgrounds' },
    { name: 'Tertiary Background', value: '#e0dbd2', usage: 'Card backgrounds' },
    { name: 'Surface', value: '#d6cfc4', usage: 'Elevated surfaces' },
    { name: 'Surface Warm', value: 'rgba(245, 240, 230, 0.95)', usage: 'Hero containers' },
  ],
  text: [
    { name: 'Primary', value: '#2a2520', usage: 'Main text (dark brown)' },
    { name: 'Secondary', value: '#4a4035', usage: 'Supporting text' },
    { name: 'Muted', value: '#7a6f60', usage: 'Subtle text' },
    { name: 'Accent', value: '#8b7355', usage: 'Warm accent text' },
  ],
  borders: [
    { name: 'Subtle', value: 'rgba(0, 0, 0, 0.06)', usage: 'Faint borders' },
    { name: 'Default', value: 'rgba(0, 0, 0, 0.12)', usage: 'Standard borders' },
    { name: 'Strong', value: 'rgba(0, 0, 0, 0.15)', usage: 'Emphasized borders' },
    { name: 'Warm Frame', value: 'rgba(220, 180, 120, 0.5)', usage: 'Hero containers' },
    { name: 'Accent Border', value: 'rgba(180, 140, 80, 0.4)', usage: 'Warm accent borders' },
  ],
  accents: [
    { name: 'Warm Amber', value: 'rgba(180, 140, 80, 0.5)', usage: 'Hover effects' },
    { name: 'Golden Glow', value: 'rgba(200, 160, 100, 0.35)', usage: 'Subtle glow' },
    { name: 'Sun Orange', value: 'rgba(220, 170, 90, 0.6)', usage: 'Active states' },
    { name: 'Link Brown', value: '#8b7355', usage: 'Interactive links' },
  ],
};

const TYPOGRAPHY = {
  fonts: [
    {
      name: 'System UI',
      value: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      usage: 'Primary font for all UI text'
    },
    {
      name: 'LOW BATT',
      value: "'LOW BATT', -apple-system, sans-serif",
      usage: 'Display font for game titles'
    },
    {
      name: 'Georgia',
      value: "'Georgia', 'Times New Roman', serif",
      usage: 'Italic subtitles and quotes'
    },
  ],
  weights: [
    { name: 'Light', value: '300', usage: 'Celestial headers, subtle text' },
    { name: 'Regular', value: '400', usage: 'Body text' },
    { name: 'Medium', value: '500', usage: 'Labels, links' },
    { name: 'Semibold', value: '600', usage: 'Section headers' },
    { name: 'Bold', value: '700', usage: 'Emphasis, buttons' },
  ],
  letterSpacing: [
    { name: 'Tight', value: '-0.02em', usage: 'Large headings' },
    { name: 'Normal', value: '0', usage: 'Body text' },
    { name: 'Wide', value: '3px', usage: 'Uppercase labels' },
    { name: 'Celestial', value: '6px', usage: 'Section titles' },
    { name: 'Expansive', value: '8px', usage: 'Hero subtitles' },
  ],
};

const BORDERS = {
  styles: [
    { name: 'Subtle Card', css: '1px solid rgba(255, 255, 255, 0.06)', usage: 'FAQ items, subtle cards' },
    { name: 'Standard Card', css: '1px solid rgba(255, 255, 255, 0.1)', usage: 'Regular cards' },
    { name: 'Settings Menu', css: '8px solid rgba(240, 245, 255, 0.85)', usage: 'Popup menus' },
    { name: 'Parallax Frame', css: '12px solid rgba(240, 245, 255, 0.9)', usage: 'Hero frames' },
    { name: 'Star Divider', css: 'linear-gradient(90deg, transparent, rgba(200, 180, 255, 0.2), transparent)', usage: 'Section dividers' },
  ],
  radii: [
    { name: 'None', value: '0', usage: 'Sharp edges' },
    { name: 'Subtle', value: '2px', usage: 'Cards, buttons' },
    { name: 'Small', value: '4px', usage: 'Inputs, tags' },
    { name: 'Medium', value: '8px', usage: 'Inner elements' },
    { name: 'Large', value: '20px', usage: 'Modals, panels, pills' },
    { name: 'Full', value: '50%', usage: 'Circular elements' },
  ],
};

const EFFECTS = {
  darkShadows: [
    {
      name: 'Glow Frame (Dark)',
      css: '0 0 25px rgba(230, 235, 250, 0.6), 0 0 50px rgba(220, 228, 245, 0.4), 0 0 80px rgba(210, 220, 240, 0.25)',
      usage: 'Parallax, hero panels'
    },
    {
      name: 'Settings Menu (Dark)',
      css: '0 0 20px rgba(230, 235, 250, 0.4), 0 0 40px rgba(220, 228, 245, 0.25), inset 0 0 20px rgba(0, 0, 0, 0.3)',
      usage: 'Dropdown menus'
    },
    {
      name: 'Button Hover (Dark)',
      css: '0 0 15px rgba(102, 126, 234, 0.6)',
      usage: 'Interactive hover states'
    },
    {
      name: 'Text Glow (Dark)',
      css: '0 0 10px rgba(220, 230, 250, 0.4)',
      usage: 'Celestial text'
    },
  ],
  lightShadows: [
    {
      name: 'Warm Frame (Light)',
      css: '0 0 25px rgba(200, 160, 100, 0.35), 0 0 50px rgba(180, 140, 80, 0.25), 0 0 80px rgba(160, 120, 60, 0.15)',
      usage: 'Hero containers'
    },
    {
      name: 'Settings Menu (Light)',
      css: '0 0 20px rgba(180, 140, 80, 0.3), 0 0 40px rgba(160, 120, 60, 0.2), inset 0 0 20px rgba(255, 255, 255, 0.3)',
      usage: 'Dropdown menus'
    },
    {
      name: 'Button Hover (Light)',
      css: '0 0 15px rgba(180, 140, 80, 0.4)',
      usage: 'Interactive hover states'
    },
    {
      name: 'Text Glow (Light)',
      css: '0 0 10px rgba(180, 140, 80, 0.3)',
      usage: 'Accent text'
    },
  ],
  gradients: [
    {
      name: 'Dark Section',
      css: 'linear-gradient(180deg, #0a0a18 0%, #12122a 50%, #0a0a18 100%)',
      usage: 'Settings menu, modals (dark)'
    },
    {
      name: 'Light Section',
      css: 'linear-gradient(180deg, #f5f3ef 0%, #eae6df 50%, #f5f3ef 100%)',
      usage: 'Settings menu, modals (light)'
    },
    {
      name: 'Warm Hero',
      css: 'linear-gradient(135deg, rgba(245, 240, 230, 0.95) 0%, rgba(235, 225, 210, 0.92) 50%, rgba(245, 240, 230, 0.95) 100%)',
      usage: 'Hero containers (light)'
    },
    {
      name: 'Purple Overlay',
      css: 'linear-gradient(135deg, rgba(60, 30, 90, 0.22) 0%, rgba(25, 15, 50, 0.15) 50%, rgba(60, 30, 90, 0.22) 100%)',
      usage: 'Image overlays (dark)'
    },
    {
      name: 'Modal Glow (Dark)',
      css: 'radial-gradient(ellipse at top, #1a1a3e 0%, #0e0e1f 40%, #0a0a18 100%)',
      usage: 'Modal backgrounds - upper glow (dark)'
    },
    {
      name: 'Modal Glow (Light)',
      css: 'radial-gradient(ellipse at top, #ffffff 0%, #f8f6f2 40%, #f5f3ef 100%)',
      usage: 'Modal backgrounds - upper glow (light)'
    },
  ],
};

function PageDesign() {
  const navigate = useNavigate();

  return (
    <div className={styles.container}>
      {/* Back button - top left */}
      <button
        className={styles.homeBackButton}
        onClick={() => { playUISound(); navigate('/game'); }}
        aria-label="Back to game"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </button>

      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Design System</h1>
        <div className={styles.themeToggle}>
          <ThemeToggle />
        </div>
      </div>

      {/* Color Palettes */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Color Palette</h2>

        <div className={styles.paletteGrid}>
          {/* Dark Mode Colors */}
          <div className={styles.paletteColumn}>
            <h3 className={styles.columnTitle}>Dark Mode</h3>

            <h4 className={styles.categoryTitle}>Backgrounds</h4>
            <div className={styles.colorGrid}>
              {DARK_MODE_COLORS.backgrounds.map((color) => (
                <div key={color.name} className={styles.colorCard}>
                  <div className={styles.colorSwatch} style={{ background: color.value }} />
                  <div className={styles.colorInfo}>
                    <span className={styles.colorName}>{color.name}</span>
                    <span className={styles.colorValue}>{color.value}</span>
                    <span className={styles.colorUsage}>{color.usage}</span>
                  </div>
                </div>
              ))}
            </div>

            <h4 className={styles.categoryTitle}>Text</h4>
            <div className={styles.colorGrid}>
              {DARK_MODE_COLORS.text.map((color) => (
                <div key={color.name} className={styles.colorCard}>
                  <div className={styles.colorSwatch} style={{ background: color.value }} />
                  <div className={styles.colorInfo}>
                    <span className={styles.colorName}>{color.name}</span>
                    <span className={styles.colorValue}>{color.value}</span>
                    <span className={styles.colorUsage}>{color.usage}</span>
                  </div>
                </div>
              ))}
            </div>

            <h4 className={styles.categoryTitle}>Borders</h4>
            <div className={styles.colorGrid}>
              {DARK_MODE_COLORS.borders.map((color) => (
                <div key={color.name} className={styles.colorCard}>
                  <div className={styles.colorSwatchBorder} style={{ background: '#080812', border: `3px solid ${color.value}` }} />
                  <div className={styles.colorInfo}>
                    <span className={styles.colorName}>{color.name}</span>
                    <span className={styles.colorValue}>{color.value}</span>
                    <span className={styles.colorUsage}>{color.usage}</span>
                  </div>
                </div>
              ))}
            </div>

            <h4 className={styles.categoryTitle}>Accents</h4>
            <div className={styles.colorGrid}>
              {DARK_MODE_COLORS.accents.map((color) => (
                <div key={color.name} className={styles.colorCard}>
                  <div className={styles.colorSwatch} style={{ background: color.value }} />
                  <div className={styles.colorInfo}>
                    <span className={styles.colorName}>{color.name}</span>
                    <span className={styles.colorValue}>{color.value}</span>
                    <span className={styles.colorUsage}>{color.usage}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Light Mode Colors */}
          <div className={styles.paletteColumn}>
            <h3 className={styles.columnTitle}>Light Mode (Mature)</h3>

            <h4 className={styles.categoryTitle}>Backgrounds</h4>
            <div className={styles.colorGrid}>
              {LIGHT_MODE_COLORS.backgrounds.map((color) => (
                <div key={color.name} className={styles.colorCard}>
                  <div className={styles.colorSwatch} style={{ background: color.value }} />
                  <div className={styles.colorInfo}>
                    <span className={styles.colorName}>{color.name}</span>
                    <span className={styles.colorValue}>{color.value}</span>
                    <span className={styles.colorUsage}>{color.usage}</span>
                  </div>
                </div>
              ))}
            </div>

            <h4 className={styles.categoryTitle}>Text</h4>
            <div className={styles.colorGrid}>
              {LIGHT_MODE_COLORS.text.map((color) => (
                <div key={color.name} className={styles.colorCard}>
                  <div className={styles.colorSwatch} style={{ background: color.value }} />
                  <div className={styles.colorInfo}>
                    <span className={styles.colorName}>{color.name}</span>
                    <span className={styles.colorValue}>{color.value}</span>
                    <span className={styles.colorUsage}>{color.usage}</span>
                  </div>
                </div>
              ))}
            </div>

            <h4 className={styles.categoryTitle}>Borders</h4>
            <div className={styles.colorGrid}>
              {LIGHT_MODE_COLORS.borders.map((color) => (
                <div key={color.name} className={styles.colorCard}>
                  <div className={styles.colorSwatchBorder} style={{ background: '#e8edf2', border: `3px solid ${color.value}` }} />
                  <div className={styles.colorInfo}>
                    <span className={styles.colorName}>{color.name}</span>
                    <span className={styles.colorValue}>{color.value}</span>
                    <span className={styles.colorUsage}>{color.usage}</span>
                  </div>
                </div>
              ))}
            </div>

            <h4 className={styles.categoryTitle}>Accents</h4>
            <div className={styles.colorGrid}>
              {LIGHT_MODE_COLORS.accents.map((color) => (
                <div key={color.name} className={styles.colorCard}>
                  <div className={styles.colorSwatch} style={{ background: color.value }} />
                  <div className={styles.colorInfo}>
                    <span className={styles.colorName}>{color.name}</span>
                    <span className={styles.colorValue}>{color.value}</span>
                    <span className={styles.colorUsage}>{color.usage}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Typography */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Typography</h2>

        <div className={styles.typographyGrid}>
          <div className={styles.typeSection}>
            <h3 className={styles.categoryTitle}>Font Families</h3>
            {TYPOGRAPHY.fonts.map((font) => (
              <div key={font.name} className={styles.fontCard}>
                <span className={styles.fontSample} style={{ fontFamily: font.value }}>
                  {font.name === 'LOW BATT' ? 'ZODIAC CHESS' : 'The quick brown fox jumps'}
                </span>
                <div className={styles.fontInfo}>
                  <span className={styles.fontName}>{font.name}</span>
                  <span className={styles.fontUsage}>{font.usage}</span>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.typeSection}>
            <h3 className={styles.categoryTitle}>Font Weights</h3>
            {TYPOGRAPHY.weights.map((weight) => (
              <div key={weight.name} className={styles.weightCard}>
                <span className={styles.weightSample} style={{ fontWeight: weight.value }}>
                  {weight.name} ({weight.value})
                </span>
                <span className={styles.weightUsage}>{weight.usage}</span>
              </div>
            ))}
          </div>

          <div className={styles.typeSection}>
            <h3 className={styles.categoryTitle}>Letter Spacing</h3>
            {TYPOGRAPHY.letterSpacing.map((spacing) => (
              <div key={spacing.name} className={styles.spacingCard}>
                <span className={styles.spacingSample} style={{ letterSpacing: spacing.value }}>
                  SAMPLE TEXT
                </span>
                <div className={styles.spacingInfo}>
                  <span className={styles.spacingName}>{spacing.name}: {spacing.value}</span>
                  <span className={styles.spacingUsage}>{spacing.usage}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Borders & Effects */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Borders & Effects</h2>

        <div className={styles.effectsGrid}>
          <div className={styles.effectSection}>
            <h3 className={styles.categoryTitle}>Border Styles</h3>
            {BORDERS.styles.map((border) => (
              <div key={border.name} className={styles.borderCard}>
                <div className={styles.borderSample} style={{ border: border.css.includes('linear') ? 'none' : border.css, background: border.css.includes('linear') ? border.css : 'transparent' }} />
                <div className={styles.borderInfo}>
                  <span className={styles.borderName}>{border.name}</span>
                  <span className={styles.borderUsage}>{border.usage}</span>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.effectSection}>
            <h3 className={styles.categoryTitle}>Border Radii</h3>
            <div className={styles.radiiGrid}>
              {BORDERS.radii.map((radius) => (
                <div key={radius.name} className={styles.radiusCard}>
                  <div className={styles.radiusSample} style={{ borderRadius: radius.value }} />
                  <span className={styles.radiusName}>{radius.name}: {radius.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.effectSection}>
            <h3 className={styles.categoryTitle}>Box Shadows (Dark Mode)</h3>
            {EFFECTS.darkShadows.map((shadow) => (
              <div key={shadow.name} className={styles.shadowCard} data-theme="dark">
                <div className={styles.shadowSample} style={{ boxShadow: shadow.css, background: '#0a0a18' }} />
                <div className={styles.shadowInfo}>
                  <span className={styles.shadowName}>{shadow.name}</span>
                  <span className={styles.shadowUsage}>{shadow.usage}</span>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.effectSection}>
            <h3 className={styles.categoryTitle}>Box Shadows (Light Mode)</h3>
            {EFFECTS.lightShadows.map((shadow) => (
              <div key={shadow.name} className={styles.shadowCard}>
                <div className={styles.shadowSample} style={{ boxShadow: shadow.css, background: '#f5f3ef' }} />
                <div className={styles.shadowInfo}>
                  <span className={styles.shadowName}>{shadow.name}</span>
                  <span className={styles.shadowUsage}>{shadow.usage}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Component Examples */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Component Examples</h2>

        <div className={styles.examplesGrid}>
          {/* Buttons */}
          <div className={styles.exampleCard}>
            <h3 className={styles.exampleTitle}>Buttons</h3>
            <div className={styles.buttonExamples}>
              <button className={styles.primaryBtn}>Primary Action</button>
              <button className={styles.secondaryBtn}>Secondary</button>
              <button className={styles.ghostBtn}>Ghost Button</button>
              <button className={styles.celestialBtn}>Celestial</button>
            </div>
          </div>

          {/* Menu Items */}
          <div className={styles.exampleCard}>
            <h3 className={styles.exampleTitle}>Menu Items</h3>
            <div className={styles.menuExamples}>
              <div className={styles.menuItem}>
                <span>Menu Item</span>
              </div>
              <div className={`${styles.menuItem} ${styles.menuItemHover}`}>
                <span>Hovered Item</span>
              </div>
              <div className={`${styles.menuItem} ${styles.menuItemActive}`}>
                <span>Active Item</span>
                <span className={styles.checkmark}>&#10003;</span>
              </div>
            </div>
          </div>

          {/* Cards */}
          <div className={styles.exampleCard}>
            <h3 className={styles.exampleTitle}>Cards</h3>
            <div className={styles.cardExamples}>
              <div className={styles.subtleCard}>
                <h4>Subtle Card</h4>
                <p>For FAQ items and lists</p>
              </div>
              <div className={styles.glowCard}>
                <h4>Glow Frame Card</h4>
                <p>For hero sections</p>
              </div>
            </div>
          </div>

          {/* Dividers */}
          <div className={styles.exampleCard}>
            <h3 className={styles.exampleTitle}>Dividers</h3>
            <div className={styles.dividerExamples}>
              <div className={styles.starDivider}>
                <span>&#10022;</span>
              </div>
              <div className={styles.lineDivider} />
              <div className={styles.gradientDivider} />
            </div>
          </div>

          {/* Text Styles */}
          <div className={styles.exampleCard}>
            <h3 className={styles.exampleTitle}>Text Styles</h3>
            <div className={styles.textExamples}>
              <h4 className={styles.celestialHeader}>CELESTIAL HEADER</h4>
              <p className={styles.bodyText}>Regular body text for content and descriptions.</p>
              <span className={styles.mutedText}>Muted supporting text</span>
              <a href="#" className={styles.linkText}>Interactive Link</a>
            </div>
          </div>

          {/* Section Headers */}
          <div className={styles.exampleCard}>
            <h3 className={styles.exampleTitle}>Section Headers</h3>
            <div className={styles.headerExamples}>
              <div className={styles.sectionHeader}>
                <span className={styles.headerStar}>&#10022;</span>
                <span className={styles.headerText}>SETTINGS</span>
                <span className={styles.headerStar}>&#10022;</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CSS Variables Reference */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>CSS Variables</h2>
        <div className={styles.codeBlock}>
          <pre>{`:root {
  /* Light Mode - Mature Gray-Blue */
  --bg-gradient-start: #e8edf2;
  --bg-gradient-end: #dce3ea;
  --bg-primary: #e8edf2;
  --bg-secondary: #dce3ea;
  --bg-tertiary: #d0d8e0;
  --bg-surface: #c5ced8;

  --text-primary: #1a2530;
  --text-secondary: #2c3e50;
  --text-muted: #5a6b7a;
  --text-accent: #3d5166;

  --border-subtle: rgba(0, 0, 0, 0.06);
  --border-default: rgba(0, 0, 0, 0.1);
  --border-strong: rgba(0, 0, 0, 0.15);
  --border-glow: rgba(255, 255, 255, 0.9);

  --accent-hover: rgba(70, 90, 120, 0.4);
  --accent-glow: rgba(80, 100, 130, 0.3);
  --link-color: #2563eb;
}

[data-theme="dark"] {
  /* Dark Mode - Celestial Night */
  --bg-gradient-start: #080812;
  --bg-gradient-end: #080812;
  --bg-primary: #080812;
  --bg-secondary: #0a0a18;
  --bg-tertiary: #12122a;
  --bg-surface: #1a1a2e;

  --text-primary: #f0f0f5;
  --text-secondary: rgba(240, 240, 245, 0.8);
  --text-muted: rgba(240, 240, 245, 0.5);
  --text-accent: rgba(240, 245, 255, 0.7);

  --border-subtle: rgba(255, 255, 255, 0.06);
  --border-default: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.15);
  --border-glow: rgba(240, 245, 255, 0.85);

  --accent-hover: rgba(200, 180, 255, 0.6);
  --accent-glow: rgba(107, 124, 138, 0.3);
  --link-color: #60a5fa;
}`}</pre>
        </div>
      </section>
    </div>
  );
}

export default PageDesign;
